import { Popover, packSx } from '@mantine/core';
import { useGetUserDateInputFormat } from 'hooks';
import { CalendarIcon } from 'icons';
import {
  type Dispatch,
  forwardRef,
  type SetStateAction,
  useState,
} from 'react';
import { TextInput } from 'shared/ui/Inputs';
import type { Props as TextInputProps } from 'shared/ui/Inputs/TextInput';
import type { DateRangeValueType } from 'types';
import { useFormatUserOnlyDate } from '../../../../hooks/useFormatUserDate';
import { CALENDAR_TYPES } from './consts';
import { DatePicker } from './DatePicker';
import { getIsParsedDateValid } from './helpers';
import { useGetIsoDateByUserFormat, useGetUserDateRegExp } from './hooks';

export type DatePickerInputValue =
  | [DateRangeValueType]
  | [DateRangeValueType, DateRangeValueType];

type Props = {
  date: DatePickerInputValue;
  setDate: Dispatch<SetStateAction<DatePickerInputValue>>;
} & TextInputProps;

/**
 * Calendar-day input. Emits values as `yyyy-MM-dd` strings (PlainDate) — no
 * time-of-day, no timezone. JS `Date` is an instant in time, not a calendar
 * day, and serializing one with `JSON.stringify` collapses local midnight
 * into UTC, which silently shifts the day west of UTC. Keeping this component
 * string-only at the boundary prevents that whole class of bug.
 *
 * If you need to capture a date *and* a time-of-day, do **not** extend this
 * component with a mode flag — build a separate `DateTimePicker` so the type
 * difference is visible at the call site.
 */
export const DatePickerInput = forwardRef<HTMLInputElement, Props>(
  ({ sx, date, setDate, ...dateRangePickerMantineProps }, ref) => {
    const userDateInputFormat = useGetUserDateInputFormat();
    const formatUserDate = useFormatUserOnlyDate();
    const userDateRegExp = useGetUserDateRegExp();
    const getIsoDateStringByUserFormat = useGetIsoDateByUserFormat();

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [calendarType, setCalendarType] = useState<
      (typeof CALENDAR_TYPES)[keyof typeof CALENDAR_TYPES]
    >(CALENDAR_TYPES.single);
    /* TODO: add initial date here */
    const isSingleData = date.length === 1;
    const formatedData = date.map(formatUserDate);
    const initialValue = isSingleData
      ? formatedData[0] || ''
      : formatedData.map(d => d || '').join(' - ');
    const [inputValue, setInputValue] = useState(initialValue);

    const resetCalendarTypeOnPopoverOpenWhenDateIsNotSelected = (
      isPopoverOpenNextState: boolean,
    ) => {
      if (isPopoverOpenNextState && !date.some(Boolean)) {
        setCalendarType(CALENDAR_TYPES.single);
      }
    };

    return (
      <Popover
        withinPortal
        opened={isPopoverOpen}
        onChange={open => {
          setIsPopoverOpen(open);
          resetCalendarTypeOnPopoverOpenWhenDateIsNotSelected(open);
        }}
      >
        <Popover.Target>
          <TextInput
            sx={[
              {
                minWidth: 305,
                '.ClearableInputRightSection_clearableIconBox': {
                  pointerEvents: 'initial',
                  touchAction: 'initial',
                },
              },
              ...packSx(sx),
            ]}
            styles={{
              rightSection: {
                pointerEvents: 'none',
                touchAction: 'none',
              },
            }}
            ref={ref}
            placeholder={`${userDateInputFormat.toUpperCase()} - ${userDateInputFormat.toUpperCase()}`}
            rightIcon={<CalendarIcon />}
            isClearableButtonVisible={isPopoverOpen && date.some(Boolean)}
            value={inputValue}
            onChange={e => {
              const { value } = e.target;

              setInputValue(value);

              if (userDateRegExp.singleDate.test(value)) {
                if (getIsParsedDateValid(value, userDateInputFormat)) {
                  setCalendarType(CALENDAR_TYPES.single);
                  setDate([getIsoDateStringByUserFormat(value)]);
                }
              } else if (userDateRegExp.fromDate.test(value)) {
                const dateFromValue = value.replace(' -', '');

                if (getIsParsedDateValid(dateFromValue, userDateInputFormat)) {
                  setCalendarType(CALENDAR_TYPES.from);

                  setDate([getIsoDateStringByUserFormat(dateFromValue), null]);
                }
              } else if (userDateRegExp.toDate.test(value)) {
                const dateFromValue = value.replace('- ', '');

                setCalendarType(CALENDAR_TYPES.to);
                setDate([null, getIsoDateStringByUserFormat(dateFromValue)]);
              } else if (userDateRegExp.rangeDate.test(value)) {
                const values = value.split(' - ') as [
                  DateRangeValueType,
                  DateRangeValueType,
                ];

                if (
                  values.every(val =>
                    getIsParsedDateValid(val as string, userDateInputFormat),
                  )
                ) {
                  setCalendarType(CALENDAR_TYPES.range);
                  setDate(
                    values.map(getIsoDateStringByUserFormat) as [
                      DateRangeValueType,
                      DateRangeValueType,
                    ],
                  );
                }
              } else {
                setCalendarType(CALENDAR_TYPES.single);
                setDate([null]);
              }
            }}
            onClick={() => {
              setIsPopoverOpen(!isPopoverOpen);
              resetCalendarTypeOnPopoverOpenWhenDateIsNotSelected(
                !isPopoverOpen,
              );
            }}
            {...dateRangePickerMantineProps}
          />
        </Popover.Target>
        <Popover.Dropdown
          sx={theme => ({
            padding: theme.other.spacing(3.5, 3, 3.5, 2),
          })}
        >
          <DatePicker
            calendarType={calendarType}
            setCalendarType={setCalendarType}
            date={date}
            setDate={setDate}
            setInputValue={setInputValue}
          />
        </Popover.Dropdown>
      </Popover>
    );
  },
);
